import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { fetcher } from "@/lib/api";
import { formatDate, formatRange } from "@/lib/format";
import { useSettings } from "@/hooks/queries";
import { PageLoader, StatusBadge } from "@/components/common";
import { StatusTimeline } from "@/components/order/StatusTimeline";
import { PaymentInfo } from "@/components/order/PaymentInfo";

const Detail = ({ label, value }) => (
  <div className="flex justify-between gap-6 py-3 text-sm">
    <span className="text-neutral-500">{label}</span>
    <span className="text-right text-neutral-100">{value}</span>
  </div>
);

const NotFound = () => (
  <section className="mx-auto max-w-3xl px-5 pb-24 pt-40 text-center" data-testid="order-not-found">
    <h1 className="font-display text-4xl">Pesanan tidak ditemukan</h1>
    <p className="mt-4 text-neutral-500">Periksa kembali kode pesananmu.</p>
    <Link to="/cek-pesanan" className="btn-gold mt-8">Cek kode lain</Link>
  </section>
);

export default function OrderDetail() {
  const { code } = useParams();
  const { data: order, isLoading, isError } = useQuery({ queryKey: ["track", code], queryFn: () => fetcher(`/orders/track/${code}`), retry: false });
  const { data: settings } = useSettings();
  if (isLoading) return <PageLoader className="min-h-screen" />;
  if (isError || !order) return <NotFound />;

  return (
    <section className="mx-auto max-w-6xl px-5 pb-24 pt-32 sm:px-8 md:pt-40" data-testid="order-detail-page">
      <p className="overline-label">{order.status === "menunggu" ? "Pesanan diterima" : "Status pesanan"}</p>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <h1 className="font-mono text-4xl tracking-wider sm:text-5xl" data-testid="order-code">{order.code}</h1>
        <button type="button" onClick={() => { navigator.clipboard?.writeText(order.code); toast.success("Kode disalin"); }} className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 hover:border-gold hover:text-gold" data-testid="order-code-copy">
          <Copy className="h-4 w-4" />
        </button>
        <StatusBadge status={order.status} testId="order-status-badge" />
      </div>
      <p className="mt-4 max-w-xl text-neutral-400">Simpan kode ini untuk mengecek status pesanan. Terima kasih, {order.customer_name}!</p>
      <div className="mt-12 grid gap-8 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-7">
          <div className="panel p-6 sm:p-8"><StatusTimeline status={order.status} /></div>
          <div className="panel divide-y divide-white/5 p-6 sm:p-8" data-testid="order-summary">
            <Detail label="Trip" value={order.trip_name} />
            <Detail label="Paket" value={order.package_name} />
            <Detail label="Tanggal" value={formatRange(order.start_date, order.end_date)} />
            <Detail label="Peserta" value={`${order.participants} orang`} />
            <Detail label="Dipesan" value={formatDate(order.created_at.slice(0, 10))} />
          </div>
        </div>
        <div className="lg:col-span-5"><PaymentInfo order={order} settings={settings} /></div>
      </div>
    </section>
  );
}
